import React, { useState, useEffect } from "react";
import { useAuth, Facility, UpdateWizardStepRequest, DetectionZone, ScreenLine, LaneLine, Users } from "@app/shared";

import * as WizardClient from "@app/shared/src/api/WizardControllerClient.gen";
import { WizardDeviceStepsEnum } from "@app/shared/src/models/Shared.WizardDeviceStepsEnum.gen";

import { useLocation, useNavigate } from "react-router-dom";
import { DashboardLayout, Pages } from "../..";
import { useFetch } from "../../../hooks";
import { CreateLocationStep, DeviceConfigurationStep, DeviceMappingStep, DeviceProvisionStep, FinishStep, LocationFacilitiesStep, LocationGroupsStep, MappingShapesStep, OrganizationStep, SetupDeviceInRealWorldStep } from "./wizardSteps/deviceSetup";
import { RequiresGoogleMaps } from "../../shared/maps/RequiresGoogleMaps";

interface WizardState {
    organizationName?: string;
    selectedGroupId?: string;
    selectedGroupName?: string;
    selectedFacilityId?: string;
    selectedFacilityName?: string;
    newFacilityData?: Partial<Facility>;
    deviceId?: string;
    createdShapes?: Array<DetectionZone | ScreenLine | LaneLine>;
}

export const DeviceSetup: React.FC = () => {
    const { currentOrganizationId } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const isNewAccount = new URLSearchParams(location.search).has("newAccount");
    const [currentStep, setCurrentStep] = useState(isNewAccount ? WizardDeviceStepsEnum.LocationGroups : WizardDeviceStepsEnum.Organization);
    const [wizardData, , fetchHelpers] = useFetch(() => WizardClient.getOrCreateWizard(currentOrganizationId), [currentOrganizationId]);
    const [wizardState, setWizardState] = useState<WizardState>({});
    const [isInitializing, setIsInitializing] = useState(false);

    const removeNewAccountParam = () => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete("newAccount");
        navigate(Pages.WizardSetupDevice.getUrl());
    };

    useEffect(() => {
        const loadWizardState = async () => {
            if (!currentOrganizationId) return;

            try {
                if (wizardData) {
                    setWizardState({
                        organizationName: wizardData.organizationName || undefined,
                        selectedGroupId: wizardData.facilityGroupId || undefined,
                        selectedGroupName: wizardData.facilityGroupName || undefined,
                        selectedFacilityId: wizardData.facilityId || undefined,
                        selectedFacilityName: wizardData.facilityName || undefined,
                        deviceId: wizardData.deviceId || undefined,
                    });

                    if (isNewAccount) {
                        setIsInitializing(true);
                        try {
                            await updateBackendStep(WizardDeviceStepsEnum.LocationGroups);
                            setCurrentStep(WizardDeviceStepsEnum.LocationGroups);
                            removeNewAccountParam();
                        } catch (error) {
                            console.error("Error updating backend step for new account:", error);
                            setCurrentStep(WizardDeviceStepsEnum.Organization);
                            removeNewAccountParam();
                        } finally {
                            setIsInitializing(false);
                        }
                    } else {
                        setCurrentStep(wizardData.currentStep);
                    }
                }
            } catch (error) {
                console.error("Error loading wizard state:", error);
                setCurrentStep(WizardDeviceStepsEnum.Organization);
                setIsInitializing(false);
            }
        };

        loadWizardState();
    }, [wizardData, isNewAccount, currentOrganizationId]);

    const updateBackendStep = async (step: WizardDeviceStepsEnum, additionalData?: { facilityGroupId?: string; facilityId?: string; deviceId?: string }) => {
        if (!currentOrganizationId) return;

        try {
            const request = new UpdateWizardStepRequest({
                organizationId: currentOrganizationId,
                step: step,
                facilityGroupId: additionalData?.facilityGroupId ?? wizardState.selectedGroupId,
                facilityId: additionalData?.facilityId ?? wizardState.selectedFacilityId,
                deviceId: additionalData?.deviceId ?? wizardState.deviceId,
            });
            await WizardClient.updateStep(request);
        } catch (error) {
            console.error("Error updating wizard step:", error);
            throw error;
        }
    };

    const handleOrganizationNext = async (selectedOrgId: string, organizationName: string) => {
        setWizardState((prev) => ({
            ...prev,
            organizationName,
        }));

        try {
            // if the organization is different, we need to switch organizations properly
            // this will trigger a page redirect, but its necessary to switch context
            if (selectedOrgId !== currentOrganizationId) {
                await WizardClient.updateStep(
                    new UpdateWizardStepRequest({
                        organizationId: selectedOrgId,
                        step: WizardDeviceStepsEnum.LocationGroups,
                        facilityGroupId: undefined,
                        facilityId: undefined,
                        deviceId: undefined,
                    })
                );
                await Users.setLastViewedOrganizationId(selectedOrgId);

                window.location.href = Pages.WizardSetupDevice.getUrl();
            } else {
                await updateBackendStep(WizardDeviceStepsEnum.LocationGroups);
                setCurrentStep(WizardDeviceStepsEnum.LocationGroups);
            }
        } catch (error) {
            console.error("DeviceSetup: Error in handleOrganizationNext", error);
        }
    };

    const handleLocationGroupNext = async (selectedGroupId?: string, groupName?: string) => {
        if (selectedGroupId) {
            setWizardState((prev) => ({
                ...prev,
                selectedGroupId,
                selectedGroupName: groupName,
            }));

            await updateBackendStep(WizardDeviceStepsEnum.LocationFacilities, { facilityGroupId: selectedGroupId });
        }
        setCurrentStep(WizardDeviceStepsEnum.LocationFacilities);
    };

    const handleLocationFacilitiesNext = async (selectedFacilityId?: string, selectedFacilityName?: string, newFacilityData?: Partial<Facility>) => {
        if (selectedFacilityId) {
            setWizardState((prev) => ({
                ...prev,
                selectedFacilityId,
                selectedFacilityName,
            }));

            await updateBackendStep(WizardDeviceStepsEnum.DeviceProvision, { facilityId: selectedFacilityId });
            setCurrentStep(WizardDeviceStepsEnum.DeviceProvision);
        } else if (newFacilityData) {
            setCurrentStep(WizardDeviceStepsEnum.CreateLocation);
        }
    };

    const handleCreateLocationNext = async (createdFacilityId: string, createdFacilityName: string | undefined) => {
        setWizardState((prev) => ({
            ...prev,
            selectedFacilityId: createdFacilityId,
            selectedFacilityName: createdFacilityName,
            newFacilityData: undefined,
        }));

        await updateBackendStep(WizardDeviceStepsEnum.DeviceProvision, { facilityId: createdFacilityId });
        setCurrentStep(WizardDeviceStepsEnum.DeviceProvision);
    };

    const handleDeviceProvisionNext = async (deviceId: string | undefined) => {
        setWizardState((prev) => ({
            ...prev,
            deviceId,
        }));

        await updateBackendStep(WizardDeviceStepsEnum.SetupDeviceInRealWorld, { deviceId });
        setCurrentStep(WizardDeviceStepsEnum.SetupDeviceInRealWorld);
    };

    const handleContinueToMapping = async () => {
        await updateBackendStep(WizardDeviceStepsEnum.DeviceToMap);
        setCurrentStep(WizardDeviceStepsEnum.DeviceToMap);
    };

    const handleContinueToShapes = async () => {
        await updateBackendStep(WizardDeviceStepsEnum.MappingShapes);
        setCurrentStep(WizardDeviceStepsEnum.MappingShapes);
    };

    const handleShapeMappingNext = async (shapes: Array<DetectionZone | ScreenLine | LaneLine>) => {
        setWizardState((prev) => ({
            ...prev,
            createdShapes: shapes,
        }));

        await updateBackendStep(WizardDeviceStepsEnum.DeviceConfiguration);
        setCurrentStep(WizardDeviceStepsEnum.DeviceConfiguration);
    };

    const handleDeviceConfigurationNext = async () => {
        await updateBackendStep(WizardDeviceStepsEnum.Finish);
        setCurrentStep(WizardDeviceStepsEnum.Finish);
    };

    const handleDisassociateDevice = async () => {
        setWizardState((prev) => ({
            ...prev,
            selectedFacilityId: undefined,
            selectedFacilityName: undefined,
            deviceId: undefined,
        }));
        await WizardClient.updateStep(
            new UpdateWizardStepRequest({
                organizationId: currentOrganizationId,
                step: WizardDeviceStepsEnum.LocationFacilities,
                facilityGroupId: wizardState.selectedGroupId,
                facilityId: undefined,
                deviceId: undefined,
            })
        );
        setCurrentStep(WizardDeviceStepsEnum.LocationFacilities);
    };

    const handleBack = async (step: WizardDeviceStepsEnum) => {
        setCurrentStep(step);
        await updateBackendStep(step);
    };

    const handleResetWizard = async () => {
        if (!currentOrganizationId) return;

        try {
            await WizardClient.reset(currentOrganizationId);
            setWizardState({});
            setCurrentStep(WizardDeviceStepsEnum.Organization); // Reset to organization step
        } catch (error) {
            console.error("Error resetting wizard:", error);
        }
    };

    if (fetchHelpers.isLoading || isInitializing) {
        return (
            <DashboardLayout centerChildren="Device Setup">
                <div className="container mx-auto px-4 py-8">
                    <div className="max-w-2xl mx-auto text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"></div>
                        <p className="text-gray-600">{isInitializing ? "Initializing wizard for new account..." : "Loading wizard state..."}</p>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    const renderCurrentStep = () => {
        switch (currentStep) {
            case WizardDeviceStepsEnum.Organization:
                return <OrganizationStep onNext={handleOrganizationNext} />;

            case WizardDeviceStepsEnum.LocationGroups:
                return <LocationGroupsStep organizationName={wizardState.organizationName || ""} organizationId={currentOrganizationId || ""} onNext={handleLocationGroupNext} onBack={() => handleBack(WizardDeviceStepsEnum.Organization)} />;

            case WizardDeviceStepsEnum.LocationFacilities:
                return <LocationFacilitiesStep selectedGroupId={wizardState.selectedGroupId!} groupName={wizardState.selectedGroupName!} onNext={handleLocationFacilitiesNext} onBack={() => handleBack(WizardDeviceStepsEnum.LocationGroups)} />;

            case WizardDeviceStepsEnum.CreateLocation:
                return <CreateLocationStep facilityGroupId={wizardState.selectedGroupId!} groupName={wizardState.selectedGroupName!} onNext={handleCreateLocationNext} onBack={() => handleBack(WizardDeviceStepsEnum.LocationFacilities)} />;

            case WizardDeviceStepsEnum.DeviceProvision:
                return <DeviceProvisionStep facilityId={wizardState.selectedFacilityId!} facilityName={wizardState.selectedFacilityName} groupName={wizardState.selectedGroupName!} onNext={handleDeviceProvisionNext} onBack={() => handleBack(WizardDeviceStepsEnum.LocationFacilities)} />;

            case WizardDeviceStepsEnum.SetupDeviceInRealWorld:
                return <SetupDeviceInRealWorldStep facilityName={wizardState.selectedFacilityName} groupName={wizardState.selectedGroupName!} deviceId={wizardState.deviceId!} onNext={handleContinueToMapping} onBack={handleDisassociateDevice} />;

            case WizardDeviceStepsEnum.DeviceToMap:
                return (
                    <RequiresGoogleMaps>
                        <DeviceMappingStep facilityId={wizardState?.selectedFacilityId || ""} deviceId={wizardState.deviceId!} onNext={handleContinueToShapes} onBack={() => handleBack(WizardDeviceStepsEnum.SetupDeviceInRealWorld)} />
                    </RequiresGoogleMaps>
                );

            case WizardDeviceStepsEnum.MappingShapes:
                return (
                    <RequiresGoogleMaps>
                        <MappingShapesStep facilityId={wizardState.selectedFacilityId!} deviceId={wizardState.deviceId!} onContinue={handleShapeMappingNext} onBack={() => handleBack(WizardDeviceStepsEnum.DeviceToMap)} />
                    </RequiresGoogleMaps>
                );

            case WizardDeviceStepsEnum.DeviceConfiguration:
                return <DeviceConfigurationStep deviceId={wizardState.deviceId!} facilityId={wizardState.selectedFacilityId!} facilityName={wizardState.selectedFacilityName} createdShapes={wizardState.createdShapes} onNext={handleDeviceConfigurationNext} onBack={() => handleBack(WizardDeviceStepsEnum.MappingShapes)} />;

            case WizardDeviceStepsEnum.Finish:
                return <FinishStep facilityId={wizardState.selectedFacilityId!} facilityName={wizardState.selectedFacilityName} resetWizard={handleResetWizard} />;

            default:
                return <OrganizationStep onNext={handleOrganizationNext} />;
        }
    };

    return (
        <DashboardLayout centerChildren="Device Setup">
            <div className="container mx-auto px-4 py-8">{renderCurrentStep()}</div>
        </DashboardLayout>
    );
};
